import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { borderDefinition, smallBorderRadius } from '@guryou/html-theme';
import { useEffectOnError, useEffectOnLoading, useEffectOnSuccess } from '@guryou/redux';
import { getValueByPath, setValueByPath } from '@guryou/utils';
import { useTheme } from '@material-ui/core/styles';
import { capitalCase } from 'change-case';
import PropTypes from 'prop-types';
import { ContainedButton } from '../buttons';
import { Block, DefaultText } from '../content';
import { FullScreenSpinner } from '../spinner';
import { serialize } from './utils/formSerializationService';
import validateForm from './utils/formValidationService';
import { FormActions } from './Wrappers';
const parseForm = (form, parserVersion) => {
  if (parserVersion !== 2) {
    return serialize(form, {
      hash: true,
      empty: true
    });
  }
  const values = {};
  Array.from(form.querySelectorAll('input,textarea,select')).forEach(element => {
    if (element.type === 'checkbox' && !element.checked) {
      return;
    }
    let existingValue = getValueByPath(values, element.getAttribute('name'));
    if (!existingValue && existingValue !== 0) {
      setValueByPath(values, element.getAttribute('name'), element.getAttribute('number') === 'true' ? +element.value : element.value);
      return;
    }
    if (!Array.isArray(existingValue)) {
      existingValue = [existingValue];
    }
    setValueByPath(values, element.getAttribute('name'), [...existingValue, element.getAttribute('number') === 'true' ? +element.value : element.value]);
  });
  return values;
};
const Form = ({
  children,
  onSubmit,
  onChange,
  className,
  actions = null,
  title,
  loading,
  parserVersion,
  stateSelector,
  enableDebounce,
  debounceTimeout = 500
}) => {
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const [form, setForm] = useState();
  const [validationSummaries, setValidationSummaries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  useEffectOnError(stateSelector, errors => {
    setIsLoading(false);
    if (!errors) {
      setValidationSummaries([]);
      return;
    }
    const summaries = [];
    errors.forEach(e => {
      if (!e.inputs?.length) {
        if (e.affected && e.reason) {
          if (e.reason === 'customMemRules') {
            //membership rules custom error
            const type = t(`GENERAL.${e.affected[0]}`);
            const period = t(`MEMBERSHIP_RULES.${e.affected[1]}`);
            summaries.push(t(`VALIDATIONS.${e.reason}`, {
              type: type,
              period: period,
              amount: e.affected[2]
            }));
          } else {
            const affectedFields = e.affected?.map(x => capitalCase(x)).join(', ');
            summaries.push(`${t(`VALIDATIONS.${e.reason}`)}: ${affectedFields}`);
          }
        } else if (e.message) {
          summaries.push(t(`VALIDATIONS.${e.message}`));
        }
      } else {
        e.inputs.forEach(input => {
          const affectedFields = !Array.isArray(input.affected) ? [capitalCase(input.affected)] : input.affected?.map(x => capitalCase(x));
          if (input.code === 'IV.ST') {
            affectedFields.forEach(name => summaries.push(t(`VALIDATIONS.${input.code.replace('.', '_')}`, {
              name,
              value: 0
            })));
          } else {
            summaries.push(`${t(`VALIDATIONS.${input.code}`)}: ${affectedFields.join(', ')}`);
          }
        });
      }
    });
    setValidationSummaries(summaries);
  });
  useEffectOnSuccess(stateSelector, () => setIsLoading(false));
  useEffectOnLoading(stateSelector, () => setIsLoading(true));
  const assignFormRef = useCallback(form => {
    if (form) setForm(form);
  }, []);
  const handleSubmit = event => {
    event.preventDefault();
    const data = parseForm(event.target, parserVersion);
    if (event.nativeEvent?.submitter?.name) {
      data[event.nativeEvent.submitter.name] = event.nativeEvent.submitter.value;
    }
    const isFormValid = validateForm(event.target);
    if (onSubmit && isFormValid) {
      onSubmit(data, event);
    }
  };
  const handleChange = () => {
    if (!onChange) {
      return;
    }
    const onChangeMethod = () => {
      const data = parseForm(form, parserVersion);
      onChange(data);
    };
    if (!enableDebounce) {
      onChangeMethod();
      return;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const id = setTimeout(() => onChangeMethod(), debounceTimeout);
    setTimeoutId(id);
  };
  let renderActions = actions !== false && actions;
  if (actions === null) {
    renderActions = /*#__PURE__*/React.createElement(FormActions, null, onSubmit && /*#__PURE__*/React.createElement(ContainedButton, {
      marginLeft: "auto",
      marginRight: "auto",
      type: "submit",
      loading: loading,
      text: t('GENERAL.save')
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, isLoading && /*#__PURE__*/React.createElement(FullScreenSpinner, null), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit,
    onChange: handleChange,
    ref: assignFormRef
  }, title && /*#__PURE__*/React.createElement("h2", {
    className: "text-center"
  }, title), !!validationSummaries?.length && /*#__PURE__*/React.createElement(Block, {
    marginLeft: 3,
    marginRight: 3,
    width: "100% !important"
  }, validationSummaries.map((m, i) => /*#__PURE__*/React.createElement(Block, {
    key: i,
    border: borderDefinition(theme.palette.error.main),
    borderRadius: smallBorderRadius,
    padding: 2,
    width: "fit-content"
  }, /*#__PURE__*/React.createElement(DefaultText, {
    color: theme.palette.error.main
  }, m)))), /*#__PURE__*/React.createElement("div", {
    className: className
  }, children), renderActions));
};
Form.propTypes = {
  children: PropTypes.any,
  onSubmit: PropTypes.func,
  onSkip: PropTypes.func,
  className: PropTypes.string,
  actions: PropTypes.any,
  loading: PropTypes.bool,
  saveText: PropTypes.string
};
export default Form;